/**
 * Chrome for the admin pages: sport-scoped sidebar + content column.
 *
 * The Next version of this was a server component that awaited the sport list
 * and the current sport's masters before rendering, and read the active path
 * from a header its middleware set. None of those three things exist in a
 * static SPA, so all of it moves into the browser:
 *
 *  * the two fetches become one `useApi` each,
 *  * the path comes from the router rather than `x-current-path`,
 *  * the remembered sport comes from the cookie in the browser rather than
 *    from `cookies()` on the server — see below, it is not optional,
 *  * who is signed in comes from GET /auth/me (see `lib/api/auth`), and the
 *    sidebar's footer signs them out.
 *
 * The sidebar still degrades to an empty list when the admin service is down,
 * because the shell has to render for the pages inside it to show their own
 * error states.
 */

import { useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";

import { Sidebar, type MasterNavItem, type SportNavItem } from "@/components/Sidebar";
import { signOut } from "@/lib/api/auth";
import { listDefinitions, listMasters } from "@/lib/api/client";
import { readRememberedSport } from "@/lib/sportCookie";
import { useApi } from "@/spa/useApi";
import { useAuth } from "@/spa/useAuth";

import styles from "./AdminLayout.module.css";
import { isWorkspace } from "@/spa/isWorkspace";

/** Fold the definition rows — one per version — into one entry per sport. */
function toSportNav(defs: Awaited<ReturnType<typeof listDefinitions>>): SportNavItem[] {
  const publishedByCode = new Map<string, boolean>();
  const shapeByCode = new Map<string, { partOf?: string | null; groups?: boolean }>();
  for (const d of defs) {
    publishedByCode.set(
      d.sportCode,
      (publishedByCode.get(d.sportCode) ?? false) || d.status === "PUBLISHED",
    );
    shapeByCode.set(d.sportCode, { partOf: d.partOf, groups: d.groups });
  }
  return [...publishedByCode]
    .map(([code, published]) => ({ code, published, ...shapeByCode.get(code) }))
    .sort((a, b) => a.code.localeCompare(b.code));
}

export function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { sport } = useParams<{ sport?: string }>();
  const auth = useAuth();

  /**
   * The sport to fall back on when the route does not name one.
   *
   * Plenty of routes are not sport-scoped — a fixture, the scoring console, the
   * roles list — and the sidebar keeps its sport only in component state. So a
   * full page load of one of those started with no sport, showed "All sports",
   * and then its own effect wrote that emptiness back over the cookie: one such
   * load and the sport was gone for good, including on the pages that do scope
   * by it.
   *
   * Read once, on mount. After that the sidebar owns the value — it tracks the
   * URL, remembers the last sport itself, and rewrites the cookie — so
   * re-reading here would fight it.
   */
  const [remembered] = useState(readRememberedSport);

  // Both allowed to fail: an unreachable admin service must still leave a shell
  // for the page inside it to report the failure in.
  const sports = useApi(() => listDefinitions().then(toSportNav).catch(() => [] as SportNavItem[]), []);
  // The menu follows the route's sport, or the remembered one when the route
  // names none — the same order the sidebar itself resolves in.
  const menuSport = sport ?? remembered ?? undefined;
  const masters = useApi(
    () =>
      menuSport
        ? listMasters(menuSport).catch(() => [] as MasterNavItem[])
        : Promise.resolve([] as MasterNavItem[]),
    [menuSport],
  );

  const workspace = isWorkspace(location.pathname);
  const account = auth.status === "ready" ? auth.account : null;
  const accountNote = account
    ? account.role
    : auth.status === "ready" && !auth.required
      ? "Sign-in is off"
      : "Not signed in";

  return (
    <div className={styles.shell} data-workspace={workspace || undefined}>
      {!workspace && (
        <Sidebar
          sports={sports.data ?? []}
          masters={masters.data ?? []}
          rememberedSport={sport ?? remembered}
          signedInAs={account?.name ?? null}
          accountNote={accountNote}
          onSignOut={
            account
              ? () => {
                  void signOut().then(() => navigate("/login"));
                }
              : undefined
          }
        />
      )}
      <main className={styles.main} data-workspace={workspace || undefined}>
        <div className={styles.content}>
          <Outlet />
        </div>
      </main>
    </div>
  );
}
